/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

export interface ScheduleFilterValues {
  division: string[]
  team: string[]
  location: string[]
  surface: string[]
  status: string[]
}

export const DEFAULT_FILTER_VALUES: ScheduleFilterValues = {
  division: [],
  team: [],
  location: [],
  surface: [],
  status: [],
}

interface FilterOption {
  label: string
  value: string
}

interface ScheduleFiltersProps {
  divisions?: FilterOption[]
  teams?: FilterOption[]
  locations?: FilterOption[]
  surfaces?: FilterOption[]
  statuses?: FilterOption[]
  filters: ScheduleFilterValues
  onFilterChange: (filters: ScheduleFilterValues) => void
}

// Dropdown arrow SVG
const DropdownArrow: React.FC<{ rotated?: boolean }> = ({
  rotated = false,
}) => (
  <svg
    width={24}
    height={24}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    css={css`
      transform: ${rotated ? 'rotate(180deg)' : 'rotate(0deg)'};
      transition: transform 0.2s ease;
    `}
  >
    <path d='M7 10L12 15L17 10H7Z' fill={colors.WHITE} />
  </svg>
)

// ─── Checkbox SVG ──────────────────────────────────────────────
const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <div
    css={css`
      width: 18px;
      height: 18px;
      border-radius: 4px;
      border: 1.5px solid ${checked ? colors.PRIMARY_300 : colors.SECONDARY_200};
      background: ${checked ? colors.PRIMARY_300 : 'transparent'};
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background 0.15s ease, border-color 0.15s ease;
    `}
  >
    {checked && (
      <svg width={12} height={12} viewBox='0 0 12 12' fill='none'>
        <path
          d='M2.5 6L5 8.5L9.5 3.5'
          stroke={colors.WHITE}
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    )}
  </div>
)

// ─── Dropdown Component ──────────────────────────────────────

interface FilterDropdownProps {
  label: string
  value: string[]
  allLabel: string
  options: FilterOption[]
  onChange: (value: string[]) => void
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  value,
  allLabel,
  options,
  onChange,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      setOpen(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, handleClickOutside])

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const displayValue = useMemo(() => {
    if (value.length === 0) {
      return allLabel
    }
    if (value.length <= 2) {
      return value
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ')
    }
    return `${value.length} selected`
  }, [value, allLabel, options])

  return (
    <div
      ref={ref}
      css={css`
        flex: 1 0 0;
        display: flex;
        flex-direction: column;
        gap: 4px;
        align-items: flex-start;
        min-width: 0;
        position: relative;
      `}
    >
      <p css={labelStyles}>{label}</p>
      <div
        css={selectStyles}
        onClick={() => setOpen((prev) => !prev)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((prev) => !prev)
          }
        }}
      >
        <p css={selectTextStyles}>{displayValue}</p>
        <div css={iconStyles}>
          <DropdownArrow rotated={open} />
        </div>
      </div>
      {open && (
        <div css={dropdownMenuStyles}>
          <div
            css={dropdownItemStyles(value.length === 0)}
            onClick={() => {
              onChange([])
            }}
          >
            <Checkbox checked={value.length === 0} />
            <span>{allLabel}</span>
          </div>
          {options.map((option) => {
            const isSelected = value.includes(option.value)
            return (
              <div
                key={option.value}
                css={dropdownItemStyles(isSelected)}
                onClick={() => {
                  toggleOption(option.value)
                }}
              >
                <Checkbox checked={isSelected} />
                <span>{option.label}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────

const labelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.WHITE};
  margin: 0;
`

const selectStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 8px 16px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  width: 100%;
  cursor: pointer;
`

const selectTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.WHITE};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`

const iconStyles = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const dropdownMenuStyles = css`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  margin-top: 4px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`

const dropdownItemStyles = (isSelected: boolean) => css`
  padding: 10px 16px;
  font-family: 'Inter', sans-serif;
  font-weight: ${isSelected ? 600 : 400};
  font-size: 14px;
  line-height: 20.3px;
  color: ${isSelected ? colors.PRIMARY_300 : colors.WHITE};
  cursor: pointer;
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${colors.SECONDARY_300};
  }

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`

const clearAllBtnStyles = css`
  flex-shrink: 0;
  align-self: flex-end;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: ${colors.PRIMARY_300};
  background: transparent;
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 8px;
  padding: 9px 16px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${colors.PRIMARY_300};
    color: ${colors.WHITE};
  }
`

/**
 * Schedule Filters Component
 * Displays functional filter dropdowns for Division, Team, Location, Surface, and Status
 * Populated from generated game data
 */
const ScheduleFilters: React.FC<ScheduleFiltersProps> = ({
  divisions = [],
  teams = [],
  locations = [],
  surfaces = [],
  statuses = [],
  filters,
  onFilterChange,
}) => {
  const handleChange =
    (key: keyof ScheduleFilterValues) => (value: string[]) => {
      onFilterChange({ ...filters, [key]: value })
    }

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => v.length > 0),
    [filters]
  )

  return (
    <div
      css={css`
        display: flex;
        gap: 16px;
        align-items: flex-end;
        width: 100%;
        padding: 16px 0;
      `}
    >
      <FilterDropdown
        label='Division'
        value={filters.division}
        allLabel='All Divisions'
        options={divisions}
        onChange={handleChange('division')}
      />
      <FilterDropdown
        label='Team'
        value={filters.team}
        allLabel='All Teams'
        options={teams}
        onChange={handleChange('team')}
      />
      <FilterDropdown
        label='Location'
        value={filters.location}
        allLabel='All Locations'
        options={locations}
        onChange={handleChange('location')}
      />
      <FilterDropdown
        label='Surface'
        value={filters.surface}
        allLabel='All Surfaces'
        options={surfaces}
        onChange={handleChange('surface')}
      />
      {statuses.length > 0 && (
        <FilterDropdown
          label='Status'
          value={filters.status}
          allLabel='All Status'
          options={statuses}
          onChange={handleChange('status')}
        />
      )}
      {hasActiveFilters && (
        <button
          css={clearAllBtnStyles}
          onClick={() => onFilterChange(DEFAULT_FILTER_VALUES)}
          type='button'
        >
          Clear All
        </button>
      )}
    </div>
  )
}

export { FilterDropdown }
export type { FilterOption }
export default ScheduleFilters
